
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Star, Navigation, Award } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SoftSkill } from "@/components/SoftSkillsBadges";

type Caregiver = {
  id: string;
  name: string;
  role: string;
  image: string;
  rating: number;
  specialties: string[];
  softSkills?: SoftSkill[];
  isPremium?: boolean;
  status?: "available" | "busy" | "on-duty";
};

const FeaturedCaregivers: React.FC = () => {
  const featuredCaregivers: Caregiver[] = [
    {
      id: "cg-123",
      name: "Sarah Johnson",
      role: "Certified Home Health Aide",
      image: "https://source.unsplash.com/WLUHO9A_xik/400x400",
      rating: 4.8,
      specialties: ["Elder Care", "Mobility Assistance"],
      softSkills: [
        { name: "Empathy", level: "expert", icon: "hand-heart" },
        { name: "Communication", level: "advanced", icon: "graduation-cap" }
      ],
      isPremium: true,
      status: "available"
    },
    {
      id: "cg-456",
      name: "Robert Chen",
      role: "Licensed Practical Nurse",
      image: "https://source.unsplash.com/7YVZYZeITc8/400x400",
      rating: 4.9,
      specialties: ["Chronic Disease Management", "Wound Care"],
      softSkills: [
        { name: "Clinical Reasoning", level: "expert", icon: "graduation-cap" },
        { name: "Leadership", level: "advanced", icon: "award" }
      ],
      isPremium: true,
      status: "on-duty"
    },
    {
      id: "cg-789",
      name: "Maria Rodriguez",
      role: "Physical Therapist",
      image: "https://source.unsplash.com/yEQ9TOaL5FM/400x400",
      rating: 4.7,
      specialties: ["Rehabilitation", "Mobility Training"],
      softSkills: [
        { name: "Adaptability", level: "advanced", icon: "thumbs-up" },
        { name: "Patience", level: "expert", icon: "award" }
      ],
      status: "available"
    },
    {
      id: "cg-101",
      name: "David Washington",
      role: "Senior Care Specialist",
      image: "https://source.unsplash.com/rDEOVtE7vOs/400x400",
      rating: 4.9,
      specialties: ["Dementia Care", "Companionship"],
      softSkills: [
        { name: "Compassion", level: "expert", icon: "hand-heart" },
        { name: "Problem Solving", level: "intermediate", icon: "thumbs-up" }
      ],
      isPremium: true,
      status: "busy"
    }
  ];

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase();
  };

  return (
    <section className="py-20 bg-care-50 relative overflow-hidden">
      <div className="absolute -right-32 bottom-0 w-64 h-64 bg-care-100 rounded-full opacity-50 blur-3xl" />
      
      <div className="container-custom relative">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-block py-1 px-3 rounded-full text-sm font-medium bg-care-100 text-care-800 mb-4">
              Expert Care
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 tracking-tight text-balance">
              Our Featured Caregivers
            </h2>
            <p className="text-muted-foreground text-lg mx-auto text-balance">
              Meet our highly rated professional caregivers, each bringing unique expertise and compassionate care.
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
          {featuredCaregivers.map((caregiver, index) => (
            <motion.div
              key={caregiver.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ 
                duration: 0.5,
                delay: index * 0.1,
                ease: [0.45, 0, 0.2, 1]
              }}
            >
              <Link to={`/caregiver/${caregiver.id}`} className="block transition-transform hover:scale-[1.02]">
                <Card className="overflow-hidden h-full">
                  <CardContent className="p-6 flex flex-col">
                    <div className="flex gap-4">
                      <div className="relative">
                        <Avatar className="h-20 w-20 rounded-full border-2 border-care-100">
                          <AvatarImage src={caregiver.image} alt={caregiver.name} />
                          <AvatarFallback>{getInitials(caregiver.name)}</AvatarFallback>
                        </Avatar>
                        {caregiver.status && (
                          <div className="absolute bottom-0 right-0 flex items-center gap-1.5">
                            <span className={`h-3 w-3 rounded-full border-2 border-white ${
                              caregiver.status === 'available' ? 'bg-green-500' : 
                              caregiver.status === 'on-duty' ? 'bg-blue-500' : 'bg-gray-400'
                            }`} />
                            <span className="text-xs bg-white px-1.5 py-0.5 rounded-full shadow-sm border border-gray-100 whitespace-nowrap">
                              {caregiver.status === 'available' ? 'Available' : 
                               caregiver.status === 'on-duty' ? 'On duty' : 'Busy'}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col">
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold text-xl text-care-800">{caregiver.name}</h3>
                          {caregiver.isPremium && (
                            <span className="flex items-center text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">
                              <Star className="h-3 w-3 mr-1 fill-amber-500" />
                              Premium
                            </span>
                          )}
                        </div>
                        <p className="text-care-600 text-sm">{caregiver.role}</p>
                        <div className="flex items-center gap-1 my-1">
                          <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                          <span className="text-sm font-medium">{caregiver.rating}</span>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {caregiver.specialties.map((specialty, i) => (
                            <span key={i} className="text-xs px-2 py-1 bg-care-100 text-care-700 rounded-full">
                              {specialty}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    {caregiver.softSkills && caregiver.softSkills.length > 0 && (
                      <div className="mt-4 pt-3 border-t border-gray-100">
                        <div className="flex flex-wrap gap-1">
                          {caregiver.softSkills.map((skill, i) => {
                            const badgeColor = skill.level === 'expert' 
                              ? 'bg-amber-100 text-amber-700 border-amber-200' 
                              : skill.level === 'advanced'
                                ? 'bg-purple-100 text-purple-700 border-purple-200'
                                : 'bg-blue-100 text-blue-700 border-blue-200';
                            
                            return (
                              <Badge 
                                key={i} 
                                variant="outline" 
                                className={`${badgeColor} text-xs`}
                              >
                                <Award className="h-3 w-3 mr-1" />
                                {skill.name}
                              </Badge>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </Link>
            </motion.div>
          ))}
        </div>
        
        <div className="text-center mt-10">
          <Button asChild className="btn-secondary">
            <Link to="/caregivers">View All Caregivers</Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default FeaturedCaregivers;
